import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { Space, Row, Col, Form, Button, Popover, Upload } from 'antd';
import { UploadFile } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import { ColorPicker } from 'antd';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../../../shared/utils/getCroppedImg';
import CkEditor from '../../../../shared/components/CkEditor';
import { ModalVisibility } from '../../ExpandedMapView';
import './GeneralTab.scss';

interface GeneralTabProps {
  settings: any;
  updateSettings: (key: string, value: any) => void;
  isThumbnailModalVisible: boolean;
  showModal: (modalType: keyof ModalVisibility) => void;
  handleCancel: (modalType: keyof ModalVisibility) => void;
  mapScreenshot: string;
  onCapture: () => void;
  onMapPositionClick: (position: string) => void;
}

interface FormData {
  mapTitle: string;
  mapDescription: string;
}

export interface GeneralTabRef {
  /** Returns form values when valid; null when map title is missing. */
  submit: () => FormData | null;
}

const getPlainTextFromHtml = (html?: string) => {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return (temp.textContent || temp.innerText || '').replace(/\u00a0/g, ' ').trim();
};

const isMapTitleEmpty = (html?: string) => !getPlainTextFromHtml(html);

const GeneralTab = forwardRef<GeneralTabRef, GeneralTabProps>((props, ref) => {
  const { settings, updateSettings, showModal, handleCancel, mapScreenshot, onCapture, onMapPositionClick } = props;

  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isAdjustingImage, setIsAdjustingImage] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [thumbnailFileName, setThumbnailFileName] = useState<string | undefined>(undefined);
  const [uploadedLogoImage, setUploadedLogoImage] = useState<string>('');
  const [isCroppingLogoImage, setIsCroppingLogoImage] = useState(false);
  const [descCrop, setDescCrop] = useState({ x: 0, y: 0 });
  const [descZoom, setDescZoom] = useState(1);
  const [descCroppedAreaPixels, setDescCroppedAreaPixels] = useState<any>(null);
  const [logoFileName, setLogoFileName] = useState<string | undefined>(undefined);
  const [mapTitleError, setMapTitleError] = useState<string | null>(null);

  const { setValue, watch, getValues } = useForm<FormData>({
    defaultValues: {
      mapTitle: settings?.mapTitle || '',
      mapDescription: settings?.mapDescription || '',
    },
  });

  const formValues = watch();

  const handleMapTitleChange = (val: string) => {
    setValue('mapTitle', val);
    if (mapTitleError && !isMapTitleEmpty(val)) {
      setMapTitleError(null);
    }
  };

  const handleImageUpload = (info: UploadChangeParam<UploadFile<any>>) => {
    const file = info.file.originFileObj as Blob;
    setThumbnailFileName(info.file.name);
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setUploadedImageUrl(result);
      // updateSettings("thumbnailImage", result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    if (file) {
      reader.readAsDataURL(file);
    }
    handleCancel('thumbnail');
  };

  const handleLogoImageUpload = (info: UploadChangeParam<UploadFile<any>>) => {
    const file = info.file.originFileObj as Blob;
    setLogoFileName(info.file.name);
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setUploadedLogoImage(result);
      setIsCroppingLogoImage(true);
    };
    if (file) reader.readAsDataURL(file);
  };

  const onDescCropComplete = (_: any, croppedAreaPixels: any) => {
    setDescCroppedAreaPixels(croppedAreaPixels);
  };

  const handleSaveLogoImage = async () => {
    if (uploadedLogoImage && descCroppedAreaPixels) {
      // getCroppedImg now handles the base64 check safely
      const croppedBlob = await getCroppedImg(uploadedLogoImage, descCroppedAreaPixels);

      if (croppedBlob) {
        const fileName = logoFileName ?? 'mapLogo.jpg';
        const file = new File([croppedBlob], fileName, { type: 'image/jpeg' });
        const croppedUrl = URL.createObjectURL(croppedBlob);

        setUploadedLogoImage(croppedUrl);
        setIsCroppingLogoImage(false);

        // Pass the actual File object to the parent for the Multipart upload
        updateSettings('mapLogo', file);
      }
    }
  };

  const uploadProps = {
    beforeUpload(file: any) {
      updateSettings('thumbnailImage', file);
    },
  };

  const handleColorChange = (color: any) => {
    updateSettings('selectedColor', color.toHexString());
  };

  //@ts-ignore
  const handleBasemapClick = () => {
    updateSettings('selectedBasemap', !settings.selectedBasemap);
  };

  const handleOpenBaseLayerPicker = () => {
    // Simulate a click on the BaseLayerPicker
    const baseLayerPickerButton = document.querySelectorAll('.cesium-button');
    console.log(baseLayerPickerButton);
    if (baseLayerPickerButton) {
      (baseLayerPickerButton[4] as HTMLElement).click();
    }
  };

  const handleMapPositionClick = (position: string) => {
    onMapPositionClick(position);
    updateSettings('selectedMapPosition', position);
  };

  const handleAdjustImagePosition = () => {
    setIsAdjustingImage(true);
    handleCancel('thumbnail');
  };
  const [loading, setloading] = useState(false);
  const handleSaveAdjustment = async () => {
    setloading(true);
    if (thumbnailSrc && croppedAreaPixels) {
      const croppedBlob = await getCroppedImg(thumbnailSrc, croppedAreaPixels);
      if (croppedBlob) {
        const fileName = thumbnailFileName ?? 'thumbnail.jpg';
        const file = new File([croppedBlob], fileName, { type: 'image/jpeg' });
        setUploadedImageUrl(URL.createObjectURL(croppedBlob));
        updateSettings('thumbnailImage', file);
      }
    }
    setloading(false);
    setIsAdjustingImage(false);
  };

  const onCropComplete = (_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  useImperativeHandle(ref, () => ({
    submit: () => {
      const values = getValues();
      if (isMapTitleEmpty(values.mapTitle)) {
        setMapTitleError('Map title is required');
        return null;
      }
      setMapTitleError(null);
      return values;
    },
  }));

  useEffect(() => {
    const contentElement = document.querySelector('.ant-layout-content');
    if (contentElement) {
      (contentElement as HTMLElement).style.backgroundColor = settings?.selectedColor;
    }
  }, [settings?.selectedColor]);

  useEffect(() => {
    if (settings?.mapLogo && typeof settings.mapLogo === 'string' && !settings.mapLogo.endsWith('/') && !uploadedLogoImage) {
      setUploadedLogoImage(settings.mapLogo);
    }
  }, [settings?.mapLogo, uploadedLogoImage]);

  const thumbnailSrc = uploadedImageUrl || mapScreenshot || settings?.thumbnailImage;

  const content = (
    <Space direction="vertical" className="general-tab__popover-content">
      <Button block>
        <Upload {...uploadProps} onChange={handleImageUpload} showUploadList={false}>
          Upload Photo
        </Upload>
      </Button>
      <Button block onClick={onCapture}>
        Current Map View
      </Button>
      <Button block onClick={() => handleCancel('thumbnail')}>
        Cancel
      </Button>
      {thumbnailSrc && (
        <Button block onClick={handleAdjustImagePosition}>
          Adjust Image Position
        </Button>
      )}
    </Space>
  );

  return (
    <>
      <Row className="form-container">
        <Col className="left-column">
          <Form.Item className="form-item">
            <div className={mapTitleError ? 'general-tab__title-field--error' : undefined}>
              <CkEditor id="mapTitle" label="Map Title" value={formValues.mapTitle} onChange={handleMapTitleChange} />
            </div>
            {mapTitleError && (
              <div className="general-tab__title-error" role="alert">
                {mapTitleError}
              </div>
            )}
          </Form.Item>
          <Form.Item className="form-item">
            <CkEditor
              id="mapDescription"
              label="Map Description"
              value={formValues.mapDescription}
              onChange={(val) => setValue('mapDescription', val)}
            />
          </Form.Item>
          <Form.Item className="form-item">
            <label>Map Logo</label>
            {!isCroppingLogoImage ? (
              <>
                <Upload onChange={handleLogoImageUpload} showUploadList={false} accept="image/*">
                  <Button>Upload Logo</Button>
                </Upload>

                {uploadedLogoImage && (
                  <div className="general-tab__logo-preview">
                    <img src={uploadedLogoImage} alt="Uploaded Logo" className="general-tab__logo-img" />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="general-tab__logo-cropper">
                  <Cropper
                    image={uploadedLogoImage}
                    crop={descCrop}
                    zoom={descZoom}
                    onCropChange={setDescCrop}
                    onZoomChange={setDescZoom}
                    onCropComplete={onDescCropComplete}
                    aspect={2 / 1}
                    showGrid
                  />
                </div>

                <div className="general-tab__crop-actions">
                  <Button type="primary" onClick={handleSaveLogoImage}>
                    Save Cropped Image
                  </Button>
                  <Button className="general-tab__crop-cancel" onClick={() => setIsCroppingLogoImage(false)}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </Form.Item>
          <Form.Item className="form-item">
            <div className="input-group">
              <label>Map Theme</label>
              <ColorPicker className="custom-button" value={settings?.selectedColor} onChange={handleColorChange} showText />
            </div>
          </Form.Item>
          <Form.Item className="form-item">
            <label>Default Map Position</label>
            <div className="button-group">
              <Button
                className={`custom-button ${settings?.selectedMapPosition === 'full_extent' ? 'selected-button' : ''}`}
                onClick={() => handleMapPositionClick('full_extent')}
              >
                Full Extent
              </Button>
              <Button
                className={`custom-button ${settings?.selectedMapPosition === 'current_map_view' ? 'selected-button' : ''}`}
                onClick={() => handleMapPositionClick('current_map_view')}
              >
                Current Map View
              </Button>
            </div>
          </Form.Item>
        </Col>
        <Col className="right-column">
          <Form.Item className="form-item">
            <label>Thumbnail</label>
            <div className="general-tab__thumbnail-box">
              {thumbnailSrc && isAdjustingImage ? (
                <Cropper
                  image={thumbnailSrc}
                  crop={crop}
                  zoom={zoom}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  showGrid={true}
                  aspect={2}
                />
              ) : thumbnailSrc ? (
                <img src={thumbnailSrc} alt="Thumbnail" className="general-tab__thumbnail-img" />
              ) : (
                <div className="general-tab__thumbnail-empty">No Image</div>
              )}
            </div>
            <Popover
              // open={isThumbnailModalVisible}
              className="custom-button thumbnail-button"
              content={content}
              trigger="click"
              destroyTooltipOnHide
            >
              {isAdjustingImage ? (
                <Button onClick={handleSaveAdjustment} loading={loading}>
                  Save Adjustment
                </Button>
              ) : (
                <Button onClick={() => showModal('thumbnail')}>Edit</Button>
              )}
            </Popover>
          </Form.Item>
          <Form.Item className="form-item">
            <label>Default Basemap</label>
            <Button className={`custom-button ${settings?.selectedBasemap ? 'selected-button' : ''}`} onClick={handleOpenBaseLayerPicker}>
              Select
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </>
  );
});

GeneralTab.displayName = 'GeneralTab';

export default GeneralTab;
